import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppSettings } from '../../contexts/AppSettingsContext';
import { Home, Tag, Calendar, Briefcase, User, CreditCard, PlusCircle, LogOut, Moon, Sun, Shield, Building2 } from 'lucide-react';

interface NavigationProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

export function Navigation({ currentPage, onNavigate }: NavigationProps) {
  const { profile, signOut } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { settings } = useAppSettings();

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
  ];

  if (settings?.discounts_enabled) {
    navItems.push({ id: 'discounts', label: 'Discounts', icon: Tag });
  }

  if (settings?.events_enabled) {
    navItems.push({ id: 'events', label: 'Events', icon: Calendar });
  }

  if (settings?.jobs_enabled) {
    navItems.push({ id: 'jobs', label: 'Jobs', icon: Briefcase });
  }

  navItems.push({ id: 'digital-id', label: 'PLIP ID', icon: CreditCard });

  if (profile?.user_type === 'vendor') {
    navItems.push({ id: 'business-profile', label: 'My Business', icon: Building2 });
    navItems.push({ id: 'vendor-post', label: 'Post', icon: PlusCircle });
  }

  if ((profile as any)?.is_admin) {
    navItems.push({ id: 'admin', label: 'Admin', icon: Shield });
  }

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      <nav className="hidden md:block glass-card sticky top-4 z-50 mx-4 mt-4 mb-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gradient">CommunityPerks</span>
            </div>

            <div className="flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                      currentPage === item.id
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-slate-700/50 transition-all duration-200"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => onNavigate('profile')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  currentPage === 'profile'
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <User className="w-5 h-5" />
                <span>{profile?.full_name}</span>
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all duration-200"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-card rounded-t-3xl pb-safe">
        <div className="grid grid-cols-5 gap-1 p-2">
          {navItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`flex flex-col items-center justify-center py-3 rounded-2xl transition-all duration-200 ${
                  currentPage === item.id
                    ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-slate-700/50'
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs mt-1 font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="md:hidden fixed top-4 right-4 z-40 flex items-center space-x-2">
        <button
          onClick={toggleDarkMode}
          className="glass-card p-3 rounded-xl text-gray-700 dark:text-gray-300 shadow-lg"
          aria-label="Toggle dark mode"
        >
          {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        <button
          onClick={() => onNavigate('profile')}
          className="glass-card p-3 rounded-xl text-gray-700 dark:text-gray-300 shadow-lg"
        >
          <User className="w-5 h-5" />
        </button>
      </div>
    </>
  );
}
